import { GetterTree } from 'vuex'
import { IRootState, IShoppingCartState } from '@/@types'
import CONSTANTS from '@/constants'
import store from '@/store'
import { isCertificationBundle, isSubscriptionPlan } from '@/helpers/productClassification'

const {
  PLANS,
  PREMIUM_PLANS,
  EXTENSIONS,
  TIERS,
  BLACK_FRIDAY_PRODUCT_BUNDLES,
  TCS_PROMO_PRODUCT_BUNDLES,
  SUMMER_SAVINGS_BUNDLE,
} = CONSTANTS
const { INE_TCS_BUNDLE } = TCS_PROMO_PRODUCT_BUNDLES
const { INE_SUMMER_SAVINGS_BUNDLE } = SUMMER_SAVINGS_BUNDLE

const checkIfPremiumPlusIsSelected = (state: IShoppingCartState) => {
  const selectedPlan = state?.selectedPlan as { merchant_id?: string }
  if (!selectedPlan) {
    return false
  }
  return (
    selectedPlan?.merchant_id === PLANS.PREMIUM_PLUS ||
    state?.shoppingCart.some(item => item.merchant_id === PLANS.PREMIUM_PLUS)
  )
}

const checkIfSummerSavingsBundleIsSelected = (state: IShoppingCartState) => {
  return state.shoppingCart.some(item => item.merchant_id === INE_SUMMER_SAVINGS_BUNDLE)
}

const getters: GetterTree<IShoppingCartState, IRootState> = {
  selectedPlan: state => {
    // A subscription plan is a base/promo plan or an API-flagged certification
    // bundle (so a newly launched bundle is picked as the selected plan);
    // Skill Dive and the TCS bundle are handled as their own selections.
    const excluded = [PLANS.SKILL_DIVE_ANNUAL, INE_TCS_BUNDLE]
    return (
      state.shoppingCart.find(
        item => isSubscriptionPlan(item) && !excluded.includes(item.merchant_id)
      ) || {}
    )
  },
  selectedSkillDivePlan: state => {
    const cartHasSkillDive = state.shoppingCart.find(
      item => item.merchant_id === PLANS.SKILL_DIVE_ANNUAL
    )
    return cartHasSkillDive || {}
  },
  selectedPlanCode: state => {
    const selectedPlan = state.selectedPlan as { merchant_id?: string }
    return selectedPlan.merchant_id || ''
  },
  selectedExtension: state => {
    const cartHasExtension = state.shoppingCart.find(
      item => item.merchant_id === EXTENSIONS.EXAM_EXTENSION
    )
    return cartHasExtension || {}
  },
  selectedBundle: (state, _, rootState) => {
    const fundamentalsOnCart = state.shoppingCart.find(item => item.merchant_id === PLANS.ANNUAL)
    let selectedBundle
    if (fundamentalsOnCart) {
      selectedBundle = rootState.products.availableProductBundles.find(
        item => item.slug === BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_THREE
      )
    } else {
      selectedBundle = state.shoppingCart.find(item => item.slug)
    }
    return selectedBundle || {}
  },
  bundleProductHasError: state => {
    return state.bundleProductHasError
  },
  getShoppingCart: state => {
    return state.shoppingCart
  },
  getAddOnsCertificationsList: state => {
    return state.addOnsCertificationsList
  },
  cartHasPremiumSubscription: state => {
    // A premium_plus certification bundle is a premium-level subscription too,
    // mirroring the base premium_plus plan matched by merchant_id above.
    return state.shoppingCart.some(
      item =>
        Object.values(PREMIUM_PLANS).includes(item.merchant_id) ||
        (isCertificationBundle(item) &&
          (item.tier === TIERS.PREMIUM || item.tier === TIERS.PREMIUM_PLUS))
    )
  },
  cartHasFundamentalsAnnualSubscription: state => {
    return state.shoppingCart.some(
      item =>
        [PLANS.ANNUAL, PLANS.EJPT_FUNDAMENTALS].includes(item.merchant_id) ||
        (isCertificationBundle(item) && item.tier === TIERS.FUNDAMENTALS)
    )
  },
  cartHasFundamentalsMonthlySubscription: state => {
    return state.shoppingCart.some(item => item.merchant_id === PLANS.MONTHLY)
  },
  isPremiumPlusSelected(state) {
    return checkIfPremiumPlusIsSelected(state)
  },
  selectedAddOnsByUser(state) {
    return state.selectedAddOnsByUser
  },
  isPlaceOrderDisabled(state) {
    if (!state.selectedAddOnsByUser) {
      return checkIfPremiumPlusIsSelected(state) || checkIfSummerSavingsBundleIsSelected(state)
    }
    return false
  },
  cartHasPremiumPlusSubscription: state => {
    return state.shoppingCart.some(item => item.merchant_id === PLANS.PREMIUM_PLUS)
  },
  cartHasAnySubscription: _ => {
    return (
      store.getters['shoppingCart/cartHasPremiumSubscription'] ||
      store.getters['shoppingCart/cartHasFundamentalsMonthlySubscription'] ||
      store.getters['shoppingCart/cartHasFundamentalsAnnualSubscription']
    )
  },
  cartHasOnlyPremiumSubscription: state => {
    return state.shoppingCart.some(item => item.merchant_id === PLANS.PREMIUM)
  },
  cartHasOnlyAnnualSubscription: state => {
    return state.shoppingCart.some(item => item.merchant_id === PLANS.ANNUAL)
  },
  cartHasSummerSavingsBundle: state => {
    return state.shoppingCart.some(item => item.merchant_id === INE_SUMMER_SAVINGS_BUNDLE)
  },
  onlyMerchantIds: (_state, currentGetters, _rootState, _rootGetters) => {
    const shoppingCart = currentGetters.getShoppingCart
    const allMerchantIds: string[] = []

    shoppingCart.forEach(
      (item: { merchant_id: string; addOnsOnCart?: { merchant_id: string }[] }) => {
        if (item.addOnsOnCart && item.addOnsOnCart.length) {
          item.addOnsOnCart.forEach((addOn: { merchant_id: string }) => {
            if (addOn.merchant_id && !allMerchantIds.includes(addOn.merchant_id)) {
              allMerchantIds.push(addOn.merchant_id)
            }
          })
        }
        allMerchantIds.push(item.merchant_id)
      }
    )
    return allMerchantIds
  },
  cartSavedAt: state => {
    return state.savedAt
  },
  outdatedCart: (_state, currentGetters, _rootState, rootGetters) => {
    const savedAt = currentGetters.cartSavedAt
    const savedHoursLimit = CONSTANTS.CART_SAVED_HOURS_LIMIT || 24

    if (savedAt && new Date(savedAt).getTime() < Date.now() - savedHoursLimit * 60 * 60 * 1000) {
      return true
    }

    const allMerchantIdsInCart = currentGetters.onlyMerchantIds
    const availableMerchantIds = rootGetters['products/availableMerchantIds']

    // Return true if at least one item from allMerchantIdsInCart is not present in availableMerchantIds
    return allMerchantIdsInCart.some((id: string) => !availableMerchantIds.includes(id))
  },
}

export default getters
